// src/v3/ui/datasource/datasource-page.tsx

import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Database, Filter, RefreshCw, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  Modal,
  Progress,
  qk,
  SmartTable,
  SYMBOL_FILTER_OPERATOR_OBJ,
  useEvents,
} from "../_common";
import { DATASOURCE_RESULT_STATUS_OBJ } from "../_deps";
import { t } from "../locales";
import { SymbolDetailCard as SymbolDetail } from "../symbol";
import {
  useApplyAudit,
  useAuditSymbols,
  useFreshness,
  useImportCandles,
  useImportPriceHistory,
  useImportSymbols,
  useProcessAllSymbols,
  useProcessTicker,
  useSymbolFilters,
  useSymbolsList,
  useUpdateSymbolFilter,
  type ApplyAuditResult,
  type AuditResult,
  type ImportResult,
  type SymbolFilter,
} from "./datasource.hooks";

function ResultDisplay({ result }: { result: ImportResult }) {
  if (result.status === DATASOURCE_RESULT_STATUS_OBJ.FAILED) {
    return (
      <ErrorState message={result.error ?? t("datasource.importFailed")} />
    );
  }
  return (
    <div className="mt-3 rounded-md border p-3 text-sm">
      <Badge variant="default">{result.status}</Badge>
      {result.imported !== undefined && (
        <div className="mt-2 text-muted-foreground">
          {t("datasource.importedSymbols", { count: result.imported })}
        </div>
      )}
      {result.enrichmentFailures !== undefined &&
        result.enrichmentFailures > 0 && (
          <div className="mt-1 text-muted-foreground">
            {t("datasource.enrichmentFailures", {
              count: result.enrichmentFailures,
            })}
          </div>
        )}
      {result.symbolsProcessed !== undefined && (
        <div className="mt-2 text-muted-foreground">
          {t("datasource.processedSymbols", { count: result.symbolsProcessed })}
        </div>
      )}
      {result.candlesUpserted !== undefined && (
        <div className="mt-1 text-muted-foreground">
          {t("datasource.candlesUpserted", { count: result.candlesUpserted })}
        </div>
      )}
      {result.failed && result.failed.length > 0 && (
        <div className="mt-1 text-destructive">
          {t("datasource.failedSymbols", { count: result.failed.length })}
          <ul className="ml-4 list-disc">
            {result.failed.map((f) => (
              <li key={f.symbol}>
                {f.symbol}: {f.error}
              </li>
            ))}
          </ul>
        </div>
      )}
      {result.date !== undefined && (
        <div className="mt-2 text-muted-foreground">
          {t("datasource.date")}: {result.date}
        </div>
      )}
      {result.saved !== undefined && (
        <div className="mt-1 text-muted-foreground">
          {t("datasource.candlesSaved", { count: result.saved })}
        </div>
      )}
      {result.filtered !== undefined && result.filtered > 0 && (
        <div className="mt-1 text-muted-foreground">
          {t("datasource.candlesFiltered", { count: result.filtered })}
        </div>
      )}
      {result.pageId !== undefined && (
        <div className="mt-2 text-muted-foreground">
          {t("datasource.page")}: {result.pageId}
        </div>
      )}
      {result.version !== undefined && (
        <div className="mt-2 text-muted-foreground">
          {t("datasource.strategyVersion", { version: result.version })}
        </div>
      )}
      {result.updated !== undefined && (
        <div className="mt-1 text-muted-foreground">
          {result.updated
            ? t("datasource.strategyUpdated")
            : t("datasource.strategyUnchanged")}
        </div>
      )}
      {result.successes && result.successes.length > 0 && (
        <div className="mt-2 text-muted-foreground">
          {t("datasource.successes")}: {result.successes.join(", ")}
        </div>
      )}
      {result.failures && Object.keys(result.failures).length > 0 && (
        <div className="mt-2 text-destructive">
          {t("datasource.failures")}:{" "}
          {Object.entries(result.failures)
            .map(([k, v]) => `${k}: ${v}`)
            .join("; ")}
        </div>
      )}
      {result.filteredOut && result.filteredOut.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-muted-foreground">
            {t("datasource.filteredOut", { count: result.filteredOut.length })}
          </summary>
          <ul className="ml-4 list-disc text-xs text-muted-foreground">
            {result.filteredOut.map((f) => (
              <li key={f.symbol}>
                <strong>{f.symbol}</strong>
                {f.name ? ` (${f.name})` : ""}: {f.reasons.join(", ")}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function ImportCard({
  title,
  description,
  isLoading,
  result,
  error,
  onRun,
  disabled,
  onRetryFailed,
  children,
}: {
  title: string;
  description: string;
  isLoading: boolean;
  result: ImportResult | null;
  error: string | null;
  onRun: () => void;
  disabled?: boolean;
  onRetryFailed?: () => void;
  children?: React.ReactNode;
}) {
  const hasFailedSymbols = (result?.failed?.length ?? 0) > 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">{description}</p>
        <div className="mt-3 flex items-center gap-2">
          {children}
          <Button onClick={onRun} disabled={isLoading || disabled}>
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                {t("datasource.importing")}
              </>
            ) : (
              t("datasource.runImport")
            )}
          </Button>
          {hasFailedSymbols && onRetryFailed && (
            <Button
              onClick={onRetryFailed}
              disabled={isLoading}
              variant="outline"
            >
              {t("datasource.retryFailed")}
            </Button>
          )}
        </div>
        {error !== null && <ErrorState message={error} onRetry={onRun} />}
        {result !== null && <ResultDisplay result={result} />}
      </CardContent>
    </Card>
  );
}

const EXCHANGES = ["NASDAQ", "NYSE", "AMEX"] as const;
type Exchange = (typeof EXCHANGES)[number];

export function FreshnessPanel() {
  const { data: freshness } = useFreshness();

  if (!freshness) return null;

  const STALE_DAYS = 30;
  const now = Date.now();

  function formatAge(iso: string | null): string {
    if (!iso) return t("datasource.never");
    const date = new Date(iso);
    const days = Math.floor((now - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return t("datasource.today");
    if (days === 1) return t("datasource.yesterday");
    return t("datasource.daysAgo", { count: days });
  }

  function isStale(iso: string | null): boolean {
    if (!iso) return true;
    const date = new Date(iso);
    const days = (now - date.getTime()) / (1000 * 60 * 60 * 24);
    return days > STALE_DAYS;
  }

  const items = [
    { key: "symbols", label: "Symbols", value: freshness.symbols },
    { key: "candles", label: "Candles", value: freshness.candles },
    {
      key: "priceHistory",
      label: "Price History",
      value: freshness.priceHistory,
    },
    { key: "strategies", label: "Strategies", value: freshness.strategies },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {t("datasource.dataFreshness")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
          {items.map((item) => {
            const stale = isStale(item.value);
            return (
              <div key={item.key} className="rounded-md border p-2">
                <div className="text-xs text-muted-foreground">
                  {item.label}
                </div>
                <div
                  className={
                    stale ? "font-semibold text-destructive" : "font-semibold"
                  }
                >
                  {formatAge(item.value)}
                </div>
                {stale && (
                  <div className="text-xs text-destructive">
                    {t("datasource.stale")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function SymbolFiltersPanel() {
  const { data: filters = [], isLoading, error } = useSymbolFilters();
  const filterUpdate = useUpdateSymbolFilter();
  const audit = useAuditSymbols();
  const applyAudit = useApplyAudit();
  const processTicker = useProcessTicker();
  const queryClient = useQueryClient();

  // Track filter state to detect changes after audit was run
  const filtersAtAuditRef = useRef<string | null>(null);
  const [filtersChangedSinceAudit, setFiltersChangedSinceAudit] =
    useState(false);

  // Audit UI state
  const [selectedTickers, setSelectedTickers] = useState<Set<string>>(
    new Set(),
  );
  const [auditPage, setAuditPage] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<
    { all: true } | { symbols: string[] } | null
  >(null);

  // Symbol detail modal (opened from audit table)
  const [detailTicker, setDetailTicker] = useState<string | null>(null);
  const [processingTickers, setProcessingTickers] = useState<Set<string>>(
    new Set(),
  );
  const [processFeedback, setProcessFeedback] = useState<string | null>(null);
  const [processError, setProcessError] = useState<string | null>(null);

  const AUDIT_PAGE_SIZE = 50;

  // Symbol detail query — enabled when a ticker is selected for the modal
  const {
    data: detailData,
    isLoading: detailLoading,
    error: detailQueryError,
  } = useSymbolsList(
    detailTicker
      ? {
          limit: 1,
          filters: [
            {
              column: "symbol",
              type: "text",
              value: detailTicker,
              operator: "equals",
            },
          ],
        }
      : undefined,
  );
  const detailSymbol = detailData?.items[0] ?? null;
  const detailError = detailQueryError
    ? detailQueryError.message
    : !detailLoading && !detailSymbol && detailTicker
      ? t("symbol.audit.openCardError")
      : null;

  // Detect filter changes after audit was run
  useEffect(() => {
    if (filtersAtAuditRef.current === null) return;
    const currentSig = JSON.stringify(filters);
    setFiltersChangedSinceAudit(currentSig !== filtersAtAuditRef.current);
  }, [filters]);

  function toggleEnabled(filter: SymbolFilter) {
    void filterUpdate.update({ ...filter, enabled: !filter.enabled });
  }

  function updateLabel(filter: SymbolFilter, label: string) {
    void filterUpdate.update({ ...filter, label });
  }

  function updateThreshold(
    filter: SymbolFilter,
    conditionIndex: number,
    value: string,
  ) {
    const numValue = Number(value);
    const conditions = filter.conditions.map((c, i) =>
      i === conditionIndex &&
      c.operator === SYMBOL_FILTER_OPERATOR_OBJ.GREATER_OR_EQ &&
      !isNaN(numValue)
        ? { ...c, value: numValue }
        : c,
    );
    void filterUpdate.update({ ...filter, conditions });
  }

  async function handleRunAudit() {
    setFiltersChangedSinceAudit(false);
    setSelectedTickers(new Set());
    setAuditPage(0);
    setProcessFeedback(null);
    setProcessError(null);
    await audit.run();
    filtersAtAuditRef.current = JSON.stringify(filters);
  }

  const handleOpenDetail = useCallback((ticker: string) => {
    setDetailTicker(ticker);
  }, []);

  const handleDetailChanged = useCallback(() => {
    if (detailTicker) {
      // Invalidate symbols list to refetch the detail modal data
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
    }
  }, [detailTicker, queryClient]);

  const handleProcessTicker = useCallback(
    async (ticker: string) => {
      setProcessingTickers((prev) => new Set(prev).add(ticker));
      setProcessFeedback(null);
      setProcessError(null);
      try {
        await new Promise<void>((resolve, reject) => {
          processTicker.run(ticker, {
            onSuccess: () => resolve(),
            onError: (err: Error) => reject(err),
          });
        });
        setProcessFeedback(t("symbol.audit.processSuccess"));
      } catch (e) {
        setProcessError(
          t("symbol.audit.processError", {
            message: e instanceof Error ? e.message : String(e),
          }),
        );
      } finally {
        setProcessingTickers((prev) => {
          const next = new Set(prev);
          next.delete(ticker);
          return next;
        });
      }
    },
    [processTicker],
  );

  function handleSelectTicker(ticker: string) {
    setSelectedTickers((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      return next;
    });
  }

  function handleSelectAllOnPage(items: AuditResult["filteredOut"]) {
    const pageItems = getPageItems(items);
    const allSelected = pageItems.every((i) => selectedTickers.has(i.symbol));
    setSelectedTickers((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageItems.forEach((i) => next.delete(i.symbol));
      } else {
        pageItems.forEach((i) => next.add(i.symbol));
      }
      return next;
    });
  }

  function getPageItems(items: AuditResult["filteredOut"]) {
    const start = auditPage * AUDIT_PAGE_SIZE;
    return items.slice(start, start + AUDIT_PAGE_SIZE);
  }

  function handleDeleteAll() {
    if (!audit.result || audit.result.filteredOut.length === 0) return;
    setConfirmTarget({ all: true });
    setShowConfirm(true);
  }

  function handleDeleteSelected() {
    if (selectedTickers.size === 0) return;
    setConfirmTarget({ symbols: Array.from(selectedTickers) });
    setShowConfirm(true);
  }

  async function handleConfirmDelete() {
    if (!confirmTarget) return;
    setShowConfirm(false);
    await new Promise<void>((resolve, reject) => {
      applyAudit.applyAudit(confirmTarget, {
        onSuccess: () => resolve(),
        onError: (err: Error) => reject(err),
      });
    });
    // After deletion: reset selection, re-run audit to refresh list
    setSelectedTickers(new Set());
    setAuditPage(0);
    filtersAtAuditRef.current = null;
    await audit.run();
    filtersAtAuditRef.current = JSON.stringify(filters);
  }

  const auditResult = audit.result;
  const auditFilteredOut = auditResult?.filteredOut ?? [];
  const pageItems = auditResult ? getPageItems(auditFilteredOut) : [];
  const totalPages = auditResult
    ? Math.max(1, Math.ceil(auditFilteredOut.length / AUDIT_PAGE_SIZE))
    : 1;
  const allOnPageSelected =
    pageItems.length > 0 &&
    pageItems.every((i) => selectedTickers.has(i.symbol));

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="h-4 w-4" />
            {t("datasource.symbolFilters")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t("datasource.symbolFiltersDesc")}
          </p>
          {error && <ErrorState message={error.message} />}
          {isLoading && (
            <div className="mt-2 text-sm text-muted-foreground">
              {t("datasource.loading")}
            </div>
          )}
          {!isLoading && filters.length > 0 && (
            <div className="mt-3 space-y-2">
              {filters.map((filter) => (
                <div key={filter.id} className="rounded-md border p-2 text-sm">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={filter.enabled}
                      onChange={() => toggleEnabled(filter)}
                    />
                    <input
                      type="text"
                      value={filter.label}
                      onChange={(e) => updateLabel(filter, e.target.value)}
                      className="flex-1 rounded border px-2 py-0.5 text-sm"
                      aria-label={t("datasource.filterLabel")}
                    />
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t("datasource.filterProperty")}: {filter.propertyKey}
                  </div>
                  <div className="mt-1 space-y-1">
                    {filter.conditions.map((cond, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">
                          {cond.operator}
                        </span>
                        {cond.operator ===
                          SYMBOL_FILTER_OPERATOR_OBJ.GREATER_OR_EQ && (
                          <input
                            type="number"
                            step="0.1"
                            value={String(cond.value)}
                            onChange={(e) =>
                              updateThreshold(filter, i, e.target.value)
                            }
                            className="w-24 rounded border px-1 py-0.5 text-xs"
                            aria-label={t("datasource.filterThreshold")}
                          />
                        )}
                        {cond.operator ===
                          SYMBOL_FILTER_OPERATOR_OBJ.NOT_ILIKE && (
                          <span className="text-muted-foreground">
                            {String(cond.value)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Audit section inside Symbol Filters panel */}
          <div className="mt-4 border-t pt-3">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => void handleRunAudit()}
                disabled={
                  audit.isLoading ||
                  (filtersChangedSinceAudit === false && isLoading)
                }
                variant="outline"
                size="sm"
              >
                {audit.isLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    {t("datasource.auditLoading")}
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    {t("datasource.auditSymbols")}
                  </>
                )}
              </Button>
            </div>

            {filtersChangedSinceAudit && auditResult && (
              <div className="mt-2 text-xs text-muted-foreground">
                {t("datasource.auditStale")}
              </div>
            )}

            {audit.error && (
              <ErrorState message={audit.error} className="mt-2" />
            )}

            {auditResult && !filtersChangedSinceAudit && (
              <AuditResultBlock
                result={auditResult}
                pageItems={pageItems}
                auditPage={auditPage}
                totalPages={totalPages}
                selectedTickers={selectedTickers}
                allOnPageSelected={allOnPageSelected}
                onSelectTicker={handleSelectTicker}
                onSelectAllOnPage={() =>
                  handleSelectAllOnPage(auditFilteredOut)
                }
                onPageChange={setAuditPage}
                onDeleteAll={handleDeleteAll}
                onDeleteSelected={handleDeleteSelected}
                applyLoading={applyAudit.isLoading}
                applyResult={applyAudit.result}
                applyError={applyAudit.error}
                onOpenDetail={handleOpenDetail}
                onProcessTicker={handleProcessTicker}
                processingTickers={processingTickers}
                processFeedback={processFeedback}
                processError={processError}
              />
            )}

            {showConfirm && confirmTarget && (
              <ConfirmDialog
                open={showConfirm}
                count={
                  "all" in confirmTarget
                    ? (auditResult?.filteredOut.length ?? 0)
                    : confirmTarget.symbols.length
                }
                onCancel={() => setShowConfirm(false)}
                onConfirm={() => void handleConfirmDelete()}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <Modal
        open={detailTicker !== null}
        onClose={() => {
          setDetailTicker(null);
        }}
        title={detailSymbol?.symbol}
        maxWidth="max-w-5xl"
      >
        {detailLoading && (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" />
            {t("symbol.audit.openCardLoading")}
          </div>
        )}
        {detailError && !detailLoading && <ErrorState message={detailError} />}
        {detailSymbol && !detailLoading && (
          <SymbolDetail
            row={detailSymbol}
            onSymbolChanged={handleDetailChanged}
          />
        )}
      </Modal>
    </>
  );
}

function AuditResultBlock({
  result,
  pageItems,
  auditPage,
  totalPages,
  selectedTickers,
  allOnPageSelected,
  onSelectTicker,
  onSelectAllOnPage,
  onPageChange,
  onDeleteAll,
  onDeleteSelected,
  applyLoading,
  applyResult,
  applyError,
  onOpenDetail,
  onProcessTicker,
  processingTickers,
  processFeedback,
  processError,
}: {
  result: AuditResult;
  pageItems: AuditResult["filteredOut"];
  auditPage: number;
  totalPages: number;
  selectedTickers: Set<string>;
  allOnPageSelected: boolean;
  onSelectTicker: (ticker: string) => void;
  onSelectAllOnPage: () => void;
  onPageChange: (page: number) => void;
  onDeleteAll: () => void;
  onDeleteSelected: () => void;
  applyLoading: boolean;
  applyResult: ApplyAuditResult | null;
  applyError: string | null;
  onOpenDetail: (ticker: string) => void;
  onProcessTicker: (ticker: string) => void;
  processingTickers: Set<string>;
  processFeedback: string | null;
  processError: string | null;
}) {
  const hasFilteredOut = result.filteredOut.length > 0;
  const auditColumns: ColumnDef<AuditResult["filteredOut"][number]>[] = [
    {
      id: "select",
      header: () => (
        <input
          type="checkbox"
          checked={allOnPageSelected}
          onChange={onSelectAllOnPage}
          aria-label="Select all on page"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedTickers.has(row.original.symbol)}
          onChange={() => onSelectTicker(row.original.symbol)}
          aria-label={`Select ${row.original.symbol}`}
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: "symbol",
      header: t("datasource.auditColTicker"),
      cell: ({ row }) => (
        <button
          className="text-left font-mono font-semibold underline-offset-2 hover:underline"
          onClick={() => onOpenDetail(row.original.symbol)}
          aria-label={`Open ${row.original.symbol} detail`}
        >
          {row.original.symbol}
        </button>
      ),
    },
    {
      accessorKey: "name",
      header: t("datasource.auditColName"),
      cell: ({ row }) => row.original.name ?? "",
    },
    {
      accessorFn: (row) => row.reasons.join(", "),
      id: "reasons",
      header: t("datasource.auditColReasons"),
    },
    {
      id: "actions",
      header: t("datasource.auditColActions"),
      cell: ({ row }) => {
        const isProcessing = processingTickers.has(row.original.symbol);
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onProcessTicker(row.original.symbol)}
            disabled={isProcessing || applyLoading}
            className="h-6 px-2 text-xs"
            aria-label={t("symbol.action.aria.process", {
              symbol: row.original.symbol,
            })}
          >
            <RefreshCw
              className={`h-3 w-3${isProcessing ? " animate-spin" : ""}`}
            />
            {t("symbol.action.process")}
          </Button>
        );
      },
      enableSorting: false,
    },
  ];

  return (
    <div className="mt-3 rounded-md border p-3">
      <div className="text-sm font-medium">
        {hasFilteredOut
          ? t("datasource.auditResult", {
              total: result.total,
              count: result.filteredOut.length,
            })
          : t("datasource.auditNoFilteredOut", { total: result.total })}
      </div>

      {hasFilteredOut && (
        <>
          <div className="mt-2 flex items-center gap-2">
            <Button
              onClick={onDeleteAll}
              disabled={applyLoading}
              variant="destructive"
              size="sm"
            >
              <Trash2 className="h-4 w-4" />
              {t("datasource.deleteAll", {
                count: result.filteredOut.length,
              })}
            </Button>
            {selectedTickers.size > 0 && (
              <Button
                onClick={onDeleteSelected}
                disabled={applyLoading}
                variant="destructive"
                size="sm"
              >
                <Trash2 className="h-4 w-4" />
                {t("datasource.deleteSelected", {
                  count: selectedTickers.size,
                })}
              </Button>
            )}
          </div>

          {applyLoading && (
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              {t("datasource.auditDeleting")}
            </div>
          )}

          {applyError && <ErrorState message={applyError} className="mt-2" />}

          {applyResult && (
            <div className="mt-2 rounded-md border p-2 text-sm">
              <Badge variant="default">{applyResult.status}</Badge>
              <div className="mt-1 text-muted-foreground">
                {t("datasource.auditDeleted", {
                  deleted: applyResult.deleted,
                  pinbars: applyResult.deletedPinbars,
                  analyses: applyResult.deletedAnalyses,
                  candles: applyResult.deletedCandles,
                  histories: applyResult.deletedHistories,
                  historyEntries: applyResult.deletedHistoryEntries,
                })}
              </div>
            </div>
          )}

          {processFeedback && (
            <div className="mt-2 text-sm text-muted-foreground">
              {processFeedback}
            </div>
          )}

          {processError && (
            <ErrorState message={processError} className="mt-2" />
          )}

          <div className="mt-3">
            <SmartTable
              columns={auditColumns}
              data={pageItems}
              total={pageItems.length}
              page={1}
              pageSize={Math.max(pageItems.length, 1)}
              isLoading={false}
              error={null}
              filters={[]}
              sort={[]}
              onPageChange={() => {}}
              onPageSizeChange={() => {}}
              onFiltersChange={() => {}}
              onSortChange={() => {}}
              rowIdField="symbol"
            />
          </div>

          {totalPages > 1 && (
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Button
                onClick={() => onPageChange(Math.max(0, auditPage - 1))}
                disabled={auditPage === 0}
                variant="outline"
                size="sm"
              >
                {t("datasource.auditPrev")}
              </Button>
              <span>
                {t("datasource.auditPage", {
                  page: auditPage + 1,
                  total: totalPages,
                })}
              </span>
              <Button
                onClick={() =>
                  onPageChange(Math.min(totalPages - 1, auditPage + 1))
                }
                disabled={auditPage === totalPages - 1}
                variant="outline"
                size="sm"
              >
                {t("datasource.auditNext")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ConfirmDialog({
  open,
  count,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  count: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={t("datasource.auditConfirmTitle")}
      maxWidth="max-w-md"
    >
      <div className="text-sm text-muted-foreground">
        {t("datasource.auditConfirmBody", { count })}
      </div>
      <div className="mt-4 flex items-center justify-end gap-2">
        <Button onClick={onCancel} variant="outline" size="sm">
          {t("datasource.auditConfirmCancel")}
        </Button>
        <Button onClick={onConfirm} variant="destructive" size="sm">
          {t("datasource.auditConfirmDelete")}
        </Button>
      </div>
    </Modal>
  );
}

export function DatasourcePage() {
  const symbolsImport = useImportSymbols();
  const candlesImport = useImportCandles();
  const priceHistoryImport = useImportPriceHistory();
  const processAll = useProcessAllSymbols();

  const [selectedExchanges, setSelectedExchanges] = useState<Exchange[]>([
    "NASDAQ",
    "NYSE",
  ]);
  const [candlesDate, setCandlesDate] = useState(
    new Date().toISOString().substring(0, 10),
  );
  const [priceSymbols, setPriceSymbols] = useState("AAPL,MSFT,GOOG");
  const [progress, setProgress] = useState<number | null>(null);
  const [processAllProgress, setProcessAllProgress] = useState<number | null>(
    null,
  );

  const { events } = useEvents([
    "datasource.import-symbols.progress",
    "datasource.import-price-history.progress",
    "scheduler.process-symbols.progress",
  ]);

  useEffect(() => {
    if (events.length === 0) return;
    const latest = events[events.length - 1];
    if (!latest) return;
    if (latest.type === "scheduler.process-symbols.progress") {
      if (latest.progress === 100) {
        setProcessAllProgress(null);
        if (processAll.isLoading) {
          processAll.complete({ processed: 0, total: 0, failed: 0 });
        }
      } else {
        setProcessAllProgress(latest.progress);
      }
      return;
    }
    if (latest.progress === 100) {
      setProgress(null);
    } else {
      setProgress(latest.progress);
    }
  }, [events, processAll]);

  const parsedPriceSymbols = priceSymbols
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const isPriceSymbolsValid = parsedPriceSymbols.length > 0;
  const isExchangesValid = selectedExchanges.length > 0;

  function toggleExchange(exchange: Exchange) {
    setSelectedExchanges((prev) =>
      prev.includes(exchange)
        ? prev.filter((e) => e !== exchange)
        : [...prev, exchange],
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            {t("datasource.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t("datasource.description")}
          </p>
        </CardContent>
      </Card>

      {progress !== null && (
        <Progress
          value={progress}
          label={
            events.length > 0 && events[events.length - 1]?.message
              ? events[events.length - 1]!.message!
              : t("datasource.importing")
          }
        />
      )}

      <FreshnessPanel />
      <SymbolFiltersPanel />

      <div className="grid gap-4 md:grid-cols-2">
        <ImportCard
          title={t("datasource.importSymbols")}
          description={t("datasource.importSymbolsDesc")}
          isLoading={symbolsImport.isLoading}
          result={symbolsImport.result}
          error={symbolsImport.error}
          disabled={!isExchangesValid}
          onRun={() => void symbolsImport.run(selectedExchanges)}
        >
          <div
            className="flex flex-wrap gap-2"
            aria-label={t("datasource.exchangesLabel")}
          >
            {EXCHANGES.map((exchange) => (
              <label key={exchange} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={selectedExchanges.includes(exchange)}
                  onChange={() => toggleExchange(exchange)}
                  className="rounded border"
                />
                {t(`datasource.exchange${exchange}`)}
              </label>
            ))}
          </div>
        </ImportCard>

        <ImportCard
          title={t("datasource.importCandles")}
          description={t("datasource.importCandlesDesc")}
          isLoading={candlesImport.isLoading}
          result={candlesImport.result}
          error={candlesImport.error}
          onRun={() => void candlesImport.run(candlesDate)}
        >
          <input
            type="date"
            value={candlesDate}
            onChange={(e) => setCandlesDate(e.target.value)}
            aria-label={t("datasource.dateLabel")}
            className="rounded-md border px-2 py-1 text-sm"
          />
        </ImportCard>

        <ImportCard
          title={t("datasource.importPriceHistory")}
          description={t("datasource.importPriceHistoryDesc")}
          isLoading={priceHistoryImport.isLoading}
          result={priceHistoryImport.result}
          error={priceHistoryImport.error}
          disabled={!isPriceSymbolsValid}
          onRun={() => void priceHistoryImport.run(parsedPriceSymbols)}
          onRetryFailed={() => {
            const failedSymbols =
              priceHistoryImport.result?.failed?.map((f) => f.symbol) ?? [];
            if (failedSymbols.length > 0) {
              setPriceSymbols(failedSymbols.join(","));
              void priceHistoryImport.run(failedSymbols);
            }
          }}
        >
          <input
            type="text"
            value={priceSymbols}
            onChange={(e) => setPriceSymbols(e.target.value)}
            placeholder="AAPL,MSFT,GOOG"
            aria-label={t("datasource.symbolsLabel")}
            className="w-48 rounded-md border px-2 py-1 text-sm"
          />
        </ImportCard>
      </div>

      <div className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>{t("datasource.processAll")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              {t("datasource.processAllDesc")}
            </p>
            <Button
              onClick={() => void processAll.run()}
              disabled={
                processAll.isLoading ||
                symbolsImport.isLoading ||
                candlesImport.isLoading ||
                priceHistoryImport.isLoading
              }
            >
              {processAll.isLoading
                ? t("datasource.processAllRunning")
                : t("datasource.processAll")}
            </Button>
            {processAllProgress !== null && (
              <div className="mt-3">
                <Progress value={processAllProgress} />
                <p className="mt-1 text-xs text-muted-foreground">
                  {processAllProgress}%
                </p>
              </div>
            )}
            {processAll.error && <ErrorState message={processAll.error} />}
            {processAll.result && (
              <div className="mt-3 rounded-md border p-3 text-sm">
                <Badge variant="default">completed</Badge>
                <span className="ml-2 text-muted-foreground">
                  All symbols processed. See terminal logs for details.
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
